// SNAP-ANDROID-SELFIE-HANDOFF (2026-11-03, thread takeover-completion-funnel).
//
// Snapchat's Android in-app browser never opens a file chooser for
// <input type="file">, so the selfie step (Step10SelfieUpload) is a dead end
// there: 0 of 115 Snapchat-Android sellers passed it 10-20..11-03, against 87%
// of Snapchat clicks opened in Android Chrome. Snapchat replaced Meta lead ads
// as the main traffic source on 10-27.
//
// The fix hands the SAME session to Chrome. The link carries the seller's
// idempotency key; Chrome stores it, POSTs a rejoinOnly createSession with it,
// and the BE's create-or-rejoin path (B-SESSION-STATE-CONTRACT) returns the
// same sessionId with a fresh token, or 404 without inserting when that
// session has ended. No new session row, no email re-claim, no new
// DICloak profile: nothing past Step01's createSession has run yet at the
// selfie step. Hydration then resumes at the selfie step via takeover.step.

import { __STORAGE_KEY as IDEMPOTENCY_STORAGE_KEY } from './idempotencyKey';

export const HANDOFF_PARAM = 'handoff';
// sessionStorage flag App reads to run the rejoin once, then clears.
export const HANDOFF_PENDING_KEY = 'takeover.chromeHandoff';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isSnapchatAndroidWebView(ua) {
  const s = typeof ua === 'string'
    ? ua
    : (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');
  return /Snapchat/i.test(s) && /Android/i.test(s);
}

// The current page URL with the handoff key added. Used as the "copy link"
// target and as Chrome's fallback URL.
export function buildHandoffUrl(href, key) {
  const u = new URL(href);
  u.searchParams.set(HANDOFF_PARAM, key);
  return u.toString();
}

// An Android intent:// link that opens the handoff URL in Chrome. When Chrome
// is not installed, browser_fallback_url reloads the same URL in place.
export function buildChromeIntentUrl(href, key) {
  const u = new URL(buildHandoffUrl(href, key));
  const scheme = u.protocol.replace(':', '');
  return `intent://${u.host}${u.pathname}${u.search}`
    + `#Intent;scheme=${scheme};package=com.android.chrome;`
    + `S.browser_fallback_url=${encodeURIComponent(u.toString())};end`;
}

// Runs once on App's first render. If the URL carries a valid handoff key,
// adopt it as this browser's idempotency key, mark the rejoin pending, and
// strip the param so a refresh or a shared screenshot does not carry it.
// Returns true when a rejoin is pending.
export function consumeHandoffParam(win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w) return false;
  try {
    const url = new URL(w.location.href);
    const key = url.searchParams.get(HANDOFF_PARAM);
    if (key) {
      url.searchParams.delete(HANDOFF_PARAM);
      w.history.replaceState(w.history.state, '', url.pathname + url.search + url.hash);
      if (UUID_RE.test(key)) {
        w.localStorage.setItem(IDEMPOTENCY_STORAGE_KEY, key);
        // A session stored in this browser belongs to another key; the rejoin
        // below decides which session this tab is on.
        w.localStorage.removeItem('takeover.sessionId');
        w.sessionStorage.setItem(HANDOFF_PENDING_KEY, '1');
      }
    }
    return w.sessionStorage.getItem(HANDOFF_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearHandoffPending(win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  try {
    if (w) w.sessionStorage.removeItem(HANDOFF_PENDING_KEY);
  } catch {
    /* storage failure is non-fatal */
  }
}
